import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { CalendarClock, Wrench, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Priority colors
const PRIORITY_COLORS = {
  low: 'bg-gray-500/90',
  medium: 'bg-yellow-500/90',
  high: 'bg-orange-500/90',
  critical: 'bg-red-500/90',
};

// Status colors  
const STATUS_COLORS = {
  proposed: 'border-gray-400',
  under_review: 'border-blue-400',
  approved: 'border-green-400',
  in_progress: 'border-yellow-400',
  completed: 'border-emerald-500',
  cancelled: 'border-red-400',
};

export function PlannedChanges3DOverlay({ 
  sdkInstance,
  cameraPosition,
  enabled = true,
  onChangeClick,
}) {
  const { token } = useAuth();
  const { currentTour } = useTour();
  const [changes, setChanges] = useState([]);
  const [screenPositions, setScreenPositions] = useState({});

  // Fetch planned changes with positions
  const fetchChanges = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/planned-changes?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to only show changes with positions
        const changesWithPositions = data.filter(c => c.position && c.sweep_id);
        setChanges(changesWithPositions);
      }
    } catch (error) {
      console.error('Error fetching planned changes for overlay:', error);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (enabled) {
      fetchChanges();
      // Refresh every 30 seconds
      const interval = setInterval(fetchChanges, 30000);
      return () => clearInterval(interval);
    }
  }, [enabled, fetchChanges]);

  // Convert 3D world positions to 2D screen coordinates
  const updateScreenPositions = useCallback(async () => {
    if (!changes.length || !cameraPosition?.position) return;

    const newPositions = {};
    
    for (const change of changes) {
      // Use target_position if available (actual point being marked)
      // Otherwise use position (camera position - will show when near that location)
      const changePos = change.target_position || change.position;
      
      if (changePos) {
        try {
          // Calculate vector from camera to change position
          const dx = changePos.x - cameraPosition.position.x;
          const dy = changePos.y - cameraPosition.position.y;
          const dz = changePos.z - cameraPosition.position.z;
          const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
          
          // If this is a camera position (no target_position), show label when very close
          // This indicates "there's a planned change here"
          const hasTargetPosition = !!change.target_position;
          const maxDistance = hasTargetPosition ? 20 : 3; // Show camera-based labels only when close
          
          if (distance < maxDistance && distance > 0.2) {
            // Get camera angles (Matterport uses degrees)
            const camYaw = (cameraPosition.rotation?.y || 0) * Math.PI / 180;
            const camPitch = (cameraPosition.rotation?.x || 0) * Math.PI / 180;
            
            // Rotate around Y axis (yaw)
            const viewX = dx * Math.cos(camYaw) + dz * Math.sin(camYaw);
            const viewY = dy;
            const viewZ = -dx * Math.sin(camYaw) + dz * Math.cos(camYaw);
            
            // Rotate around X axis (pitch)
            const finalY = viewY * Math.cos(camPitch) - viewZ * Math.sin(camPitch);
            const finalZ = viewY * Math.sin(camPitch) + viewZ * Math.cos(camPitch);
            
            // Show if in front of camera OR if we're at the saved position (show at top)
            if (finalZ > 0.2) {
              // Standard perspective projection for target positions
              const fovFactor = 0.8;
              const aspectRatio = window.innerWidth / window.innerHeight;
              
              const ndcX = (viewX / finalZ) * fovFactor;
              const ndcY = (finalY / finalZ) * fovFactor * aspectRatio;
              
              const screenX = 50 + ndcX * 50;
              const screenY = 50 - ndcY * 50;
              
              if (screenX >= 2 && screenX <= 98 && screenY >= 2 && screenY <= 95) {
                const scale = Math.max(0.7, Math.min(1.2, 3 / distance));
                
                newPositions[change.id] = {
                  x: screenX,
                  y: screenY,
                  visible: true,
                  distance,
                  scale,
                };
              }
            } else if (!hasTargetPosition && distance < 2) {
              // For camera-position-only changes, when we're AT that position,
              // show the label at the top center (like a "you are here" marker)
              newPositions[change.id] = {
                x: 50,
                y: 15,
                visible: true,
                distance,
                scale: 1,
                isHereMarker: true,
              };
            }
          }
        } catch (e) {
          // Silently handle calculation errors
        }
      }
    }
    
    setScreenPositions(newPositions);
  }, [changes, cameraPosition]);

  // Update screen positions when camera moves
  useEffect(() => {
    if (enabled && changes.length > 0) {
      updateScreenPositions();
    }
  }, [enabled, cameraPosition, updateScreenPositions]);

  if (!enabled || changes.length === 0) {
    return null;
  }

  const visibleChanges = changes.filter(c => screenPositions[c.id]?.visible);

  return (
    <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
      {visibleChanges.map((change) => {
        const pos = screenPositions[change.id];
        if (!pos) return null;
        
        const priorityColor = PRIORITY_COLORS[change.priority] || PRIORITY_COLORS.medium;
        const statusBorder = STATUS_COLORS[change.status] || STATUS_COLORS.proposed;
        
        return (
          <div
            key={change.id}
            className="absolute pointer-events-auto transition-all duration-150"
            style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              transform: `translate(-50%, -100%) scale(${pos.scale || 1})`,
              zIndex: pos.distance ? Math.floor(100 - pos.distance) : 80,
            }}
          >
            {/* Label container */}
            <div 
              className={cn(
                "flex flex-col items-start gap-1 px-3 py-2 rounded-lg shadow-xl",
                "backdrop-blur-md text-white",
                pos.isHereMarker ? "bg-primary/90 border-2 border-white" : "bg-black/80",
                "border-2",
                !pos.isHereMarker && statusBorder,
                "hover:scale-105 hover:bg-black/90 transition-all cursor-pointer",
                "max-w-[200px]"
              )}
              onClick={() => onChangeClick && onChangeClick(change)}
            >
              {/* "You are here" indicator for camera-position changes */}
              {pos.isHereMarker && (
                <div className="text-[9px] uppercase tracking-wider font-bold text-white/80 mb-0.5">
                  📍 Planned Change Here
                </div>
              )}
              {/* Header with icon and ID */}
              <div className="flex items-center gap-2 w-full">
                <div className={cn("p-1 rounded", priorityColor)}>
                  {change.priority === 'critical' ? (
                    <AlertTriangle className="h-3 w-3 text-white" />
                  ) : (
                    <Wrench className="h-3 w-3 text-white" />
                  )}
                </div>
                <span className="text-[10px] font-mono text-gray-400">
                  {change.change_id}
                </span>
              </div>
              
              {/* Title */}
              <span className="text-xs font-semibold leading-tight line-clamp-2">
                {change.title}
              </span>
              
              {/* Status badge */}
              <Badge 
                variant="outline" 
                className={cn(
                  "text-[9px] h-4 px-1.5",
                  change.status === 'in_progress' && "bg-yellow-500/20 text-yellow-300 border-yellow-500",
                  change.status === 'approved' && "bg-green-500/20 text-green-300 border-green-500",
                  change.status === 'proposed' && "bg-gray-500/20 text-gray-300 border-gray-500",
                  change.status === 'completed' && "bg-emerald-500/20 text-emerald-300 border-emerald-500",
                )}
              >
                {change.status?.replace('_', ' ')}
              </Badge>
            </div>
            
            {/* Pointer/stem pointing down */}
            <div className="flex justify-center">
              <div className="w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent border-t-black/80" />
            </div>
            
            {/* Pulsing dot at the exact position */}
            <div className="flex justify-center -mt-1">
              <div className="relative">
                <div className={cn(
                  "w-3 h-3 rounded-full",
                  priorityColor,
                  "animate-pulse"
                )} />
                <div className={cn(
                  "absolute inset-0 w-3 h-3 rounded-full",
                  priorityColor,
                  "animate-ping opacity-75"
                )} />
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Legend in corner */}
      {visibleChanges.length > 0 && (
        <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 text-white text-xs">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-primary" />
            <span>{visibleChanges.length} Planned Change{visibleChanges.length !== 1 ? 's' : ''} in view</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlannedChanges3DOverlay;
